import { Form, Formik, FormikHelpers } from 'formik'
import noop from 'lodash/noop'
import React, { FC, useCallback, useContext } from 'react'
import { usePopupManager } from 'react-popup-manager'
import styled from 'styled-components'
import { object, SchemaOf, string } from 'yup'

import { ButtonWithProgress } from '@Components/Button/ButtonWithProgress'
import { ErrorModal } from '@Components/ErrorModal'
import { FeedbackModal } from '@Components/FeedbackModal'
import { TextField } from '@Components/FormElements/Formik/TextField'
import { Product } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useMParticle } from '@Globals/MParticle/useMParticle'
import { up } from '@Globals/Style/breakpoints'
import { useTranslation } from '@Globals/Translations'

import { Container } from './styled'

interface SizeData {
  size: string
}

const StyledSubmitButton = styled(ButtonWithProgress)({
  width: '100%',

  [up('mobileStyleThreshold')]: {
    width: 'auto',
  },
})

const SizeSchema: SchemaOf<SizeData> = object().shape({ size: string().required() })

export interface SizeMissingProps {
  className?: HTMLDivElement['className']
  onAbort?: () => void
  product: Product
}

export const SizeMissing: FC<SizeMissingProps> = ({
  className,
  product,
  onAbort = noop,
}) => {
  const { t } = useTranslation()
  const { closeAll: closeAllModals, open: openModal } = usePopupManager()
  const { storeURL } = useContext(MetadataContext)
  const { otherEvent } = useMParticle()

  const doSubmit = useCallback(async (values: SizeData, formikHelpers: FormikHelpers<SizeData>) => {
    formikHelpers.setSubmitting(true)
    try {
      // TODO: this should probably live in graph instead!
      const url = storeURL('thsystem/requests/missingoption')
      await fetch(url, {
        body: JSON.stringify({
          message: values.size,
          productId: product.id,
        }),
        method: 'POST',
      })
      formikHelpers.setSubmitting(false)

      // Let's tell the parent component we have nothing more to do here.
      onAbort()

      // Tell the user everything went well.
      openModal(
        FeedbackModal,
        {
          onHide: closeAllModals,
          text: t('cms:PP_OUT_OF_STOCK_OVERLAY_CONTACT_YOU_SOON'),
        },
      )
      otherEvent({
        eventName: 'product_missing_size_report_res',
        attributes: {
          is_success: true,
        },
      })
    } catch (err) {
      formikHelpers.setSubmitting(false)
      onAbort()
      openModal(ErrorModal, { onHide: closeAllModals })
      otherEvent({
        eventName: 'product_missing_size_report_res',
        attributes: {
          is_success: false,
        },
      })
    }
  }, [
    closeAllModals,
    onAbort,
    openModal,
    otherEvent,
    product.id,
    storeURL,
    t,
  ])

  return (
    <Container className={className}>
      <p className="title">{t('cms:WE_ARE_SORRY_TO_HEAR_THAT')}</p>
      <p>{t('cms:SIZE_NOT_HERE_DO_BETTER_IN_FUTURE_TEXT')}</p>
      <Formik<SizeData>
        initialValues={{ size: '' }}
        onSubmit={doSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={SizeSchema}
      >
        {({ errors, isSubmitting }) => (
          <Form>
            <TextField
              error={errors.size ? t('cms:COF_YOU_ARE_MISSING_THIS_FIELD') : undefined}
              id="th-missing-size-on-product"
              label={t('cms:THE_MISSING_SIZE')}
              name="size"
              type="text"
            />
            <div className="buttonWrap">
              <StyledSubmitButton
                color="primary"
                disabled={isSubmitting}
                loading={isSubmitting}
                type="submit"
              >
                {t('cms:TELL_US')}
              </StyledSubmitButton>
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

